import { memo, useState } from 'react';
import { Select, createListCollection, Badge, Box } from '@chakra-ui/react';
import { type Column } from '@tanstack/react-table';
import { DatePicker } from '../components/DatePicker';
import { formatDateUTC, dbTimeToJsDate } from '../utils/dateUtils';
import { useTranslation } from 'react-i18next';

export const MemoizedFilter = memo(Filter) as typeof Filter;

function Filter<I>({ column, isFetching }: { column: Column<I>; isFetching: boolean }) {
	const { filterVariant } = column?.columnDef?.meta ?? {};

	if (filterVariant === 'select') {
		return <SelectFilter column={column} isFetching={isFetching} />;
	}
	if (filterVariant === 'dateTime') {
		return <DateFilter column={column} isFetching={isFetching} />;
	}
}

function SelectFilter<G>({ column, isFetching }: { column: Column<G>; isFetching: boolean }) {
	const { t } = useTranslation();
	const { filterOptions } = column?.columnDef?.meta ?? {};
	const columnHeader = typeof column?.columnDef?.header === 'string' ? column?.columnDef?.header : null;
	const filterValue = column.getFilterValue();
	const currentFiltersCount = Array.isArray(filterValue) ? filterValue.length : 0;

	const optionCollection = createListCollection({
		items: filterOptions ?? [],
	});

	return (
		<Select.Root
			key={column.id}
			multiple={true}
			disabled={isFetching}
			collection={optionCollection}
			onValueChange={({ value }) => {
				if (!value || value.length === 0) {
					column.setFilterValue(undefined);
				} else {
					column.setFilterValue(value);
				}
			}}
			value={Array.isArray(filterValue) ? filterValue : []}
			variant="outline"
			minW="150px"
			flex={0}
		>
			<Select.HiddenSelect />
			<Select.Control>
				<Select.Trigger>
					<Select.ValueText wordBreak="break-word">{column.columnDef.meta?.filterLabel || columnHeader}</Select.ValueText>
				</Select.Trigger>
				<Select.IndicatorGroup>
					{currentFiltersCount > 0 ? (
						<Badge colorPalette={'brand.primary'} variant="solid">
							{currentFiltersCount}
						</Badge>
					) : (
						<Select.Indicator />
					)}
				</Select.IndicatorGroup>
			</Select.Control>
			<Select.Positioner>
				<Select.Content>
					{optionCollection.items.length === 0 ? (
						<Select.Item item={{ value: 'empty', disabled: true }}>{t('general.no_items')}</Select.Item>
					) : (
						optionCollection.items.map(item => (
							<Select.Item wordBreak="break-word" item={item} key={item.value}>
								{item.label}
								<Select.ItemIndicator />
							</Select.Item>
						))
					)}
				</Select.Content>
			</Select.Positioner>
		</Select.Root>
	);
}

function DateFilter<J>({ column, isFetching }: { column: Column<J>; isFetching: boolean }) {
	const { t } = useTranslation();
	const filterValue = column.getFilterValue() as Array<string>;
	const filterLabel = column?.columnDef?.meta?.filterLabel;
	const filterLabelIsArray = Array.isArray(filterLabel);

	const [date, setDate] = useState<Date | null>(filterValue?.[0] ? dbTimeToJsDate(filterValue?.[0]) : null);
	const [secondDate, setSecondDate] = useState<Date | null>(filterValue?.[1] ? dbTimeToJsDate(filterValue?.[1]) : null);

	const onChangeDate = (value: Date | null, index: number) => {
		if (index === 0) setDate(value);
		if (index === 1) setSecondDate(value);
		column.setFilterValue((prev: Array<string>) => {
			const updated = [...(prev ?? [])];

			if (!value) {
				delete updated[index];
			} else {
				const formattedUtc = formatDateUTC(value).replace(':', '-');
				updated[index] = formattedUtc;
			}

			if (!updated[0] && !updated[1]) return undefined;
			return updated;
		});
	};

	if (column.columnDef.filterFn === 'dateTimeBetween') {
		return (
			<>
				<Box maxW={{ mobileDown: '100%', base: '215px' }} flexShrink={1}>
					<DatePicker
						placeholderText={filterLabelIsArray && filterLabel[0] ? filterLabel[0] : t('general.after')}
						disabled={isFetching}
						showTimeSelect
						selected={date}
						onChange={(value: Date | null) => onChangeDate(value instanceof Date ? value : null, 0)}
					/>
				</Box>
				<Box maxW={{ mobileDown: '100%', base: '215px' }} flexShrink={1}>
					<DatePicker
						placeholderText={filterLabelIsArray && filterLabel[1] ? filterLabel[1] : t('general.before')}
						disabled={isFetching}
						showTimeSelect
						selected={secondDate}
						onChange={(value: Date | null) => onChangeDate(value instanceof Date ? value : null, 1)}
					/>
				</Box>
			</>
		);
	}

	return (
		<Box maxW={{ mobileDown: '100%', base: '215px' }}>
			<DatePicker
				placeholderText={filterLabel && !filterLabelIsArray ? filterLabel : t('general.select_date')}
				disabled={isFetching}
				showTimeSelect
				selected={date}
				onChange={(value: Date | null) => onChangeDate(value instanceof Date ? value : null, 0)}
			/>
		</Box>
	);
}
